import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CustomerFilters } from "@/components/customers/CustomerFilters";
import { CustomersTable } from "@/components/customers/CustomersTable";
import { AddCustomerDialog } from "@/components/customers/AddCustomerDialog";
import { SaveSegmentDialog } from "@/components/customers/SaveSegmentDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, SlidersHorizontal, ChevronLeft, ChevronRight } from "lucide-react";

const EMPTY_FILTERS = { min_spend: "", max_spend: "", tag: "", platform: "", category: "", state: "", purchased_after: "", purchased_before: "" };

export default function CustomersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState([]);
  const [sort, setSort] = useState("last_purchase");
  const [direction, setDirection] = useState("desc");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [segDialogOpen, setSegDialogOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    api.get("/settings/categories").then((r) => setCategories(r.data.values)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q, sort, direction, page, limit: 25 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/customers", { params });
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, filters, sort, direction, page]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchData, 250);
    return () => clearTimeout(debounceRef.current);
  }, [fetchData]);

  const toggleSort = (field) => {
    if (sort === field) setDirection(direction === "desc" ? "asc" : "desc");
    else { setSort(field); setDirection("desc"); }
    setPage(1);
  };

  const onFilterChange = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const totalPages = Math.max(1, Math.ceil(data.total / 25));

  return (
    <div className="space-y-8" data-testid="customers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-serif font-semibold">Customers</h1>
          <p className="text-muted-foreground mt-2">{data.total.toLocaleString()} records</p>
        </div>
        <AddCustomerDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={(c) => navigate(`/customers/${c.id}`)} />
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search by name, email, phone, or city…"
            className="pl-10 h-11"
            data-testid="customer-search-input"
          />
        </div>
        <Button variant="secondary" className="h-11" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </Button>
      </div>

      {showFilters && (
        <CustomerFilters
          filters={filters} onFilterChange={onFilterChange} categories={categories}
          isAdmin={user?.role === "admin"}
          onClear={() => { setFilters({ ...EMPTY_FILTERS }); setPage(1); }}
          onSaveSegment={() => setSegDialogOpen(true)}
        />
      )}

      <CustomersTable data={data} loading={loading} onToggleSort={toggleSort} onRowClick={(c) => navigate(`/customers/${c.id}`)} />

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground" data-testid="pagination-info">
          Showing {data.total === 0 ? 0 : (page - 1) * 25 + 1}–{Math.min(page * 25, data.total)} of {data.total.toLocaleString()} customers · Page {page} of {totalPages}
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page-btn">
            <ChevronLeft className="h-4 w-4" /> Previous
          </Button>
          <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="next-page-btn">
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <SaveSegmentDialog open={segDialogOpen} onOpenChange={setSegDialogOpen} q={q} filters={filters} />
    </div>
  );
}
